/// <amd-module name="Build/Common/Library/Legacy/Components/PipelineSettingsPanel" />
import * as React from "react";

import { BuildDefinition, DefinitionQueueStatus, YamlProcess } from "Build/Client/Contracts/Build";
import { RepositoryDescriptor } from "Build/Common/Library/Types";
import { ConfigFilesResult, ConfigFileType, IRepositoryService } from "Build/Common/Library/RepositoryService";
import { BranchDropdown } from "Build/Common/Library/Legacy/Components/BranchDropdown";
import * as Resources from "Build/Common/Library/Legacy/Resources";
import { BuildOptionGUIDs } from "Build/Common/Library/Constants";
import { RepositoryTypes } from "Build/SourceProviders/Common";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ContributedFeature, ContributedFeatureEnabledValue, ContributedFeatureState } from "TFS/FeatureManagement/Contracts/FeatureManagement";
import { IFeatureUpdateError, ITfsFeatureService } from "TFS/FeatureManagement/TfsFeatureService";
import { ITfsPageService } from "Tfs/Platform/Page";
import { shallowCompare } from "VSS/Core/Util/Object";
import * as StringUtil from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { IButtonProps } from "VSSUI/Button";
import { ComboBox, IComboBoxOption } from "VSSUI/ComboBox";
import { TitleSize } from "VSSUI/Header";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Checkbox } from "VSSUI/Checkbox";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

interface PipelineSettingsProps {
    definition: BuildDefinition;
    branch: string;
    isYamlDirty?: boolean;
    errorMessage?: string;
    isOpen: boolean;
    isSaving: boolean;
    onDismiss: () => void;
    onSave: (changes: Partial<PipelineSettings>) => void;
    onError: (message: string) => void;
}

export interface PipelineSettings {
    status: DefinitionQueueStatus;
    yamlFilePath: string;
    witLinkingEnabled: boolean;
    witLinkingBranchFilter: string;
}

interface PipelineSettingsState {
    originalSettings: PipelineSettings;
    modifiedSettings: PipelineSettings;
    configFiles: IComboBoxOption[];
    selectedConfigFileKey: string;
    witLinkingPossible?: boolean;
    witLinkingEnabled: boolean;
    selectedWitLinkingBranch: string;
}

const AGILE_PRODUCT_CONTRIBUTION_ID = "ms.vss-work.agile";

export class PipelineSettingsPanel extends VssComponent<PipelineSettingsProps, PipelineSettingsState> {
    private _featureService: ITfsFeatureService;
    private _isFirstTimeOpened = true;

    constructor(props: PipelineSettingsProps, context: IVssComponentContext) {
        super(props, context);

        this._featureService = this.context.pageContext.getService<ITfsFeatureService>("ITfsFeatureService");

        const yamlProcess = this.props.definition.process as YamlProcess;
        const settings: PipelineSettings = {
            status: this.props.definition.queueStatus,
            yamlFilePath: yamlProcess.yamlFilename,
            witLinkingEnabled: this._isWitLinkingEnabled(this.props.definition),
            witLinkingBranchFilter: this._getExistingWitLinkingFilter(this.props.definition)
        };
        const repository = this.props.definition.repository;
        this.state = {
            originalSettings: settings,
            modifiedSettings: { ...settings },
            configFiles: this._toComboBoxOptions([settings.yamlFilePath]),
            selectedConfigFileKey: settings.yamlFilePath,
            // If it's not an Azure DevOps repo linking not possible
            witLinkingPossible: StringUtil.equals(repository.type, RepositoryTypes.TfsGit, true) ? undefined : false,
            witLinkingEnabled: settings.witLinkingEnabled,
            selectedWitLinkingBranch: this._convertToBranchSelector(settings.witLinkingBranchFilter)
        }
    }

    public render(): JSX.Element {
        const footerButtons: IButtonProps[] = [{
            text: Resources.CancelButtonText,
            disabled: this.props.isSaving,
            onClick: this.props.onDismiss,
        },
        {
            text: Resources.SaveButtonLabel,
            primary: true,
            disabled: this.props.isSaving || !(this._isDirty() && this._areAllRequiredFieldsSpecified()),
            onClick: this._onSave
        }];

        return (
            <div>
                {this.props.isOpen &&
                    <Panel
                        titleProps={{ text: Resources.PipelineSettingsPanelTitle, size: TitleSize.Large }}
                        onDismiss={this._onDismiss}
                        footerButtonProps={footerButtons}>
                        {this._renderBody()}
                    </Panel>
                }
            </div >
        );
    }

    public componentDidMount(): void {
        if(this.state.witLinkingPossible === undefined) {
            this._checkIfWitLinkingPossible();
        }
    }

    private async _checkIfWitLinkingPossible() {
        const definitions = await this._featureService.getFeatureDefinitions("ms.vss-tfs-web.product-features", true, ["product"]);
        const agileFeature = definitions.features.find((product: ContributedFeature) => product.id === AGILE_PRODUCT_CONTRIBUTION_ID);
        if(agileFeature) {
            const agileFeatureStateArray = await Promise.all(this._featureService.getFeatureStates([agileFeature], false, "project", { project: this._getProjectId() }));
            const agileFeatureState = Object.assign({}, ...agileFeatureStateArray);
            const witLinkingPossible = agileFeatureState[AGILE_PRODUCT_CONTRIBUTION_ID].state === ContributedFeatureEnabledValue.Enabled
            this.setState({ witLinkingPossible });
        } else {
            this.setState({ witLinkingPossible: false });
        }
    }

    private _getProjectId(): string {
        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();
        return pageData!.project!.id;
    }

    private _renderBody(): JSX.Element {
        if (this._isFirstTimeOpened) {
            // Fetch YAML files once only when/if the settings panel is opened.
            // This also gives time for the History observable to initialize for pageSource in requests.
            this._getConfigFiles();
            this._isFirstTimeOpened = false;
        }

        let dirtyYamlMessage = "";
        if (this.props.isYamlDirty
            && this.state.modifiedSettings.yamlFilePath !== this.state.originalSettings.yamlFilePath) {
            dirtyYamlMessage = Resources.PipelineSettingsYamlDiscard;
        }

        const repository = this.props.definition.repository;

        return (
            <div className="ci-designer-settings-panel-body flex-grow">
                {
                    this.props.errorMessage &&
                    <MessageCard
                        className="error-message"
                        severity={MessageCardSeverity.Error}>
                        {this.props.errorMessage}
                    </MessageCard>
                }
                <RadioButtonGroup
                    text={Resources.PipelineSettingsSubheaderStatus}
                    selectedButtonId={String(this.state.modifiedSettings.status)}
                    onSelect={this._onStatusChanged}>
                    <RadioButton
                        id={String(DefinitionQueueStatus.Enabled)}
                        text={Resources.PipelineSettingsStatusEnabled} />
                    <RadioButton
                        id={String(DefinitionQueueStatus.Paused)}
                        text={Resources.PipelineSettingsStatusPaused} />
                    <RadioButton
                        id={String(DefinitionQueueStatus.Disabled)}
                        text={Resources.PipelineSettingsStatusDisabled} />
                </RadioButtonGroup>
                <ComboBox
                    className="yaml-file-path-input"
                    allowFreeform={true}
                    label={Resources.PipelineSettingsYamlFilePath}
                    options={this.state.configFiles}
                    selectedKey={this.state.selectedConfigFileKey}
                    errorMessage={dirtyYamlMessage}
                    onChanged={this._onYamlFileChanged} />

                {
                    this.state.witLinkingPossible === undefined && <Spinner size={SpinnerSize.medium} className="wit-linking-spinner" />
                }
                {
                    this.state.witLinkingPossible &&
                    <Checkbox
                        className={"wit-linking-checkbox"}
                        label={Resources.AutoLinkWorkItemsOption}
                        checked={this.state.witLinkingEnabled}
                        onChange={(ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, isChecked: boolean) => {
                            this._onWitLinkToggle(isChecked);
                        }}
                    />
                }
                {
                    this.state.witLinkingPossible && this.state.witLinkingEnabled &&
                    <BranchDropdown
                        ariaLabel={Resources.BranchLabel}
                        className={"wit-linking-dropdown"}
                        repositoryType={repository.type}
                        repositoryId={repository.id}
                        defaultBranch={this.state.selectedWitLinkingBranch}
                        connectionId={repository.properties && repository.properties["connectedServiceId"]}
                        isDrodownFullWidth={true}
                        comboBoxMinWidth={120}
                        comboBoxMaxWidth={350}
                        onBranchSelected={this._onWitLinkingBranchChange}
                        renderAllBranchesPicker={true} />
                }

            </div>
        );
    }

    private _onDismiss = () => {
        // Reset the state on dismiss so the settings reflect the actual pipeline settings if re-opened.
        this.setState({
            modifiedSettings: { ...this.state.originalSettings },
            selectedConfigFileKey: this.state.originalSettings.yamlFilePath,
            selectedWitLinkingBranch: this._convertToBranchSelector(this.state.originalSettings.witLinkingBranchFilter),
            witLinkingEnabled: this.state.originalSettings.witLinkingEnabled,
        });
        this.props.onDismiss();
    }

    private _onSave = () => {
        // Pass only changed settings back for saving.
        const orig = this.state.originalSettings;
        const changes: Partial<PipelineSettings> = { ...this.state.modifiedSettings };
        changes.status === orig.status && delete changes.status
        changes.yamlFilePath === orig.yamlFilePath && delete changes.yamlFilePath
        changes.witLinkingEnabled === orig.witLinkingEnabled && delete changes.witLinkingEnabled
        changes.witLinkingBranchFilter === orig.witLinkingBranchFilter && delete changes.witLinkingBranchFilter

        this.props.onSave(changes);
    }

    private _onStatusChanged = (status: string) => {
        this.setState({
            modifiedSettings: {
                ...this.state.modifiedSettings,
                status: Number(status)
            }
        });
    }

    private _onWitLinkToggle = (status: boolean) => {
        this.setState({
            modifiedSettings: {
                ...this.state.modifiedSettings,
                witLinkingEnabled: status
            },
            witLinkingEnabled: status
        });
    }

    /**
     * Updates the YAML file and combo box options while allowing one free-form text option at the top of the list
     */
    private _onYamlFileChanged = (option: IComboBoxOption, index: number, value: string) => {
        const yamlFilePath = option && option.text || value;
        let selectedKey: string = (option && option.text) ? String(option.key) : "";
        if (!selectedKey) {
            selectedKey = "__FreeFormTextKey__";
            const configFiles = this.state.configFiles;
            let firstFile = configFiles[0];
            if (firstFile.key !== selectedKey) {
                firstFile = this._toComboBoxOption(selectedKey, value);
                configFiles.unshift(firstFile);
            }
            firstFile.text = value;
            this.setState({ configFiles });
        }

        this.setState({
            modifiedSettings: {
                ...this.state.modifiedSettings,
                yamlFilePath
            },
            selectedConfigFileKey: selectedKey
        });
    }

    private _onWitLinkingBranchChange = (newBranch: string) => {
        const branchFilter = this._convertToBranchFilter(newBranch);
        this.setState({
            modifiedSettings: {
                ...this.state.modifiedSettings,
                witLinkingBranchFilter: branchFilter
            },
            selectedWitLinkingBranch: newBranch
        });
    }

    private _getConfigFiles() {
        const definition = this.props.definition;
        const repository: RepositoryDescriptor = {
            connectionId: definition.repository.properties && definition.repository.properties["connectedServiceId"],
            id: definition.repository.id,
            name: definition.repository.name,
            sourceProviderType: definition.repository.type,
            defaultBranch: this.props.branch
        };

        const repositoryService = this.context.pageContext.getService<IRepositoryService>("IRepositoryService");
        repositoryService.getConfigFiles(repository, ConfigFileType.Yaml)
            .then((configFilesResult: ConfigFilesResult) => {
                let yamlFiles = configFilesResult.files || [];
                const currentYaml = this.state.originalSettings.yamlFilePath || "";

                // The current YAML may start with an optional slash.  Remove any dupe and put the current first.
                const prefixedYaml = currentYaml.startsWith("/") ? currentYaml : "/" + currentYaml;
                yamlFiles = yamlFiles.filter( file => file !== prefixedYaml);
                yamlFiles.unshift(currentYaml);

                this.setState({ configFiles: this._toComboBoxOptions(yamlFiles) });
            }, (error: Error) => {
                const message = (error && error.message) || Resources.PipelineSettingsYamlFetchError;
                this.props.onError(message);
            });
    }

    private _toComboBoxOptions(fileNames: string[]): IComboBoxOption[] {
        return fileNames.map((fileName) => { return this._toComboBoxOption(fileName, fileName) });
    }

    private _toComboBoxOption(key: string, text: string): IComboBoxOption {
        return { key, text };
    }

    private _isDirty(): boolean {
        return !shallowCompare(this.state.originalSettings, this.state.modifiedSettings);
    }

    private _areAllRequiredFieldsSpecified(): boolean {
        return !this.state.modifiedSettings.witLinkingEnabled || (this.state.modifiedSettings.witLinkingBranchFilter != "");
    }

    private _isWitLinkingEnabled = (definition: BuildDefinition): boolean => {
        if (definition.options) {
            for (const o of definition.options) {
                if (StringUtil.equals(o.definition.id, BuildOptionGUIDs.WitLinkingOptionGUID, true)) {
                    return o.enabled;
                }
            }
        }
        return false;
    };

    private _getExistingWitLinkingFilter = (definition: BuildDefinition): string => {
        if (definition.options) {
            for (const o of definition.options) {
                if (StringUtil.equals(o.definition.id, BuildOptionGUIDs.WitLinkingOptionGUID)) {
                    const filter = o.inputs["branchFilters"];
                    if (filter) {
                        const filterArray = JSON.parse(filter);
                        return filterArray.length > 0 ? filterArray[0] : "";
                    }
                }
            }
        }
        return "";
    };

    private _convertToBranchFilter = (versionSelectorBranch: string): string => {
        return "+refs/heads/" + versionSelectorBranch;
    }
    private _convertToBranchSelector = (branchFilter: string): string => {
        return branchFilter.replace("+refs/heads/", "");
    }
}
